import React from 'react';
import { Box, Group, Modal, Stack, Text, Divider } from '@mantine/core';
import {
  GANTT_KEYBOARD_SHORTCUT_SECTIONS,
} from '../../../gantt/ganttKeyboardShortcutsHelp';

export interface GanttKeyboardShortcutsModalProps {
  opened: boolean;
  onClose: () => void;
}

/** Key chip + description row — matches Location management shortcuts styling. */
function ShortcutRow({ keys, description }: { keys: string; description: string }) {
  return (
    <Group justify="space-between" wrap="nowrap" gap="md" py={4}>
      <Box
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 110,
          padding: '6px 10px',
          borderRadius: 8,
          background: 'var(--mantine-color-default)',
          border: '1px solid var(--mantine-color-default-border)',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--mantine-color-text)',
          flexShrink: 0,
          textAlign: 'center',
          whiteSpace: 'nowrap',
        }}
      >
        {keys}
      </Box>
      <Text size="sm" style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
        {description}
      </Text>
    </Group>
  );
}

/** Lists available Gantt keyboard shortcuts (undo/redo excluded). */
export function GanttKeyboardShortcutsModal({
  opened,
  onClose,
}: GanttKeyboardShortcutsModalProps) {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Keyboard shortcuts"
      size="md"
      centered
      radius="md"
    >
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Click the chart first so it has focus, then use these shortcuts. Undo and redo are
          not available yet.
        </Text>
        {GANTT_KEYBOARD_SHORTCUT_SECTIONS.map((section, index) => (
          <Box key={section.title}>
            {index > 0 ? <Divider my="xs" /> : null}
            <Text
              size="xs"
              fw={600}
              c="dimmed"
              tt="uppercase"
              mb={4}
              style={{ letterSpacing: '0.04em' }}
            >
              {section.title}
            </Text>
            <Stack gap={0}>
              {section.rows.map((row) => (
                <ShortcutRow
                  key={`${section.title}-${row.keys}`}
                  keys={row.keys}
                  description={row.description}
                />
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Modal>
  );
}
